import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/common";
import { GUIDE_CATEGORIES, visibleGuides } from "@/data/guides";
import { Search, ArrowRight, LifeBuoy, Sparkles, Printer } from "lucide-react";

function GuideCard({ guide }) {
  const Icon = guide.icon;
  return (
    <Link
      to={`/portal/guides/${guide.slug}`}
      data-testid={`guide-card-${guide.slug}`}
      className="group flex items-start gap-4 rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-medium leading-snug text-foreground">{guide.title}</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{guide.summary}</p>
        <div className="mt-2.5 flex items-center gap-2">
          <Badge variant="secondary" className="text-[11px] font-medium">{guide.minutes} min read</Badge>
        </div>
      </div>
      <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
    </Link>
  );
}

function FeaturedCard({ guide }) {
  const Icon = guide.icon;
  return (
    <Link
      to={`/portal/guides/${guide.slug}`}
      data-testid={`guide-featured-${guide.slug}`}
      className="group relative block overflow-hidden rounded-2xl border border-primary/30 bg-card p-6 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div className="fc-hero-bg pointer-events-none absolute inset-0 opacity-90" aria-hidden="true" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/12 text-primary">
          <Icon className="h-7 w-7" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge className="border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.18)] text-[11px] text-[#8A4A18] dark:text-[#E6A463]">
              <Sparkles className="mr-1 h-3 w-3" /> Featured
            </Badge>
            <span className="text-xs text-muted-foreground">{guide.minutes} min read</span>
          </div>
          <h3 className="mt-2 font-serif text-xl font-semibold text-foreground">{guide.title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{guide.summary}</p>
        </div>
        <ArrowRight className="hidden h-5 w-5 shrink-0 text-primary transition-transform group-hover:translate-x-0.5 sm:block" />
      </div>
    </Link>
  );
}

export default function HelpHub() {
  const { can } = useAuth();
  const [query, setQuery] = useState("");

  const all = useMemo(() => visibleGuides(can), [can]);
  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return all;
    return all.filter(
      (g) => g.title.toLowerCase().includes(q) || g.summary.toLowerCase().includes(q)
    );
  }, [all, q]);

  const featured = !q ? all.filter((g) => g.featured) : [];
  const featuredSlugs = new Set(featured.map((g) => g.slug));

  const groups = GUIDE_CATEGORIES.map((cat) => ({
    ...cat,
    items: filtered.filter((g) => g.category === cat.key && !featuredSlugs.has(g.slug)),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <header>
        <div className="flex items-center gap-2 text-primary">
          <LifeBuoy className="h-5 w-5" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em]">Help &amp; Guides</p>
        </div>
        <h1 className="mt-2 font-serif text-2xl font-semibold text-foreground sm:text-3xl">
          Everything you need to run Friday Conversations
        </h1>
        <p className="mt-1 max-w-2xl text-muted-foreground">
          Short, friendly guides for the whole app &mdash; from finding your way around to producing a
          travelling live show. You only see the guides that apply to you.
        </p>
      </header>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guides…"
          className="pl-9"
          data-testid="guides-search-input"
          aria-label="Search guides"
        />
      </div>

      {featured.length > 0 && (
        <section className="space-y-3" data-testid="guides-featured-section">
          {featured.map((g) => (
            <FeaturedCard key={g.slug} guide={g} />
          ))}
        </section>
      )}

      {groups.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No guides found"
          description={`Nothing matches “${query}”. Try a different word.`}
          testId="guides-empty"
        />
      ) : (
        <div className="space-y-10">
          {groups.map((group) => (
            <section key={group.key} className="space-y-3" data-testid={`guides-group-${group.key}`}>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                {group.items.map((g) => (
                  <GuideCard key={g.slug} guide={g} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {can("producer_tools") && (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Printer className="h-4 w-4" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Travelling to a venue?</p>
              <p className="text-sm text-muted-foreground">Print the one-page pre-show checklist and tick it off on site.</p>
            </div>
          </div>
          <Link
            to="/portal/guides/checklist"
            data-testid="guides-open-checklist-link"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-primary/40 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Open checklist <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
